import { Helmet } from 'react-helmet-async'

// Reusable SEO head-tag component for the marketing site.
//
// Pass a `title` (the brand suffix is appended automatically as
// "{title} · TempDash") OR `fullTitle` (used verbatim — useful for the
// homepage where we want a brand-led format like "TempDash | …").
// Pass a `description`, the page `path` for the canonical URL, and
// optionally a `jsonLd` object — every page that wants to show up in
// Google with structured data passes the relevant graph from
// src/lib/landingJsonLd.js.
//
// `keywords` is rendered as a <meta name="keywords"> tag. Modern Google
// largely ignores this for ranking, but Bing and a few smaller engines
// still read it, and it’s harmless when curated tightly.
//
// `noindex` is a one-off escape hatch for pages we explicitly want crawlers
// to skip (currently unused on the marketing site, but handy if a draft
// page ever lands in production).

const SITE_URL          = 'https://tempdash.app'
const DEFAULT_OG_IMAGE  = `${SITE_URL}/og/og-default.png`

export default function Seo({
  title,
  fullTitle,                     // optional — bypasses the "· TempDash" suffix
  description,
  path = '/',
  ogImage  = DEFAULT_OG_IMAGE,
  jsonLd   = null,
  keywords = null,               // optional comma-joined string
  noindex  = false,
}) {
  const url             = `${SITE_URL}${path}`
  const renderedTitle   = fullTitle ?? `${title} · TempDash`

  return (
    <Helmet prioritizeSeoTags>
      <title>{renderedTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      <meta property="og:type"        content="website" />
      <meta property="og:site_name"   content="TempDash" />
      <meta property="og:url"         content={url} />
      <meta property="og:title"       content={renderedTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image"       content={ogImage} />
      <meta property="og:image:width"  content="1200" />
      <meta property="og:image:height" content="630" />

      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content={renderedTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={ogImage} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  )
}
